import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import { useAPI } from "../../hooks/useAPI";

// ── Queries ───────────────────────────────────────────────────────────────────

export function useMarketplacePrograms(filters = {}) {
  const { authToken } = useAuth();
  const { callAPI } = useAPI();

  return useQuery({
    queryKey: ["marketplace", "programs", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.set("search", filters.search);
      // categories is an array — append each as a separate ?category= param
      (filters.categories ?? []).forEach((slug) => params.append("category", slug));
      if (filters.level) params.set("level", filters.level);
      if (filters.sort) params.set("sort", filters.sort);
      const qs = params.toString();
      const data = await callAPI(
        `marketplace${qs ? `?${qs}` : ""}`,
        { token: authToken },
      );
      return {
        stats: data.stats ?? { program_count: 0, simulation_count: 0 },
        count: data.count ?? 0,
        results: data.results ?? [],
      };
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!authToken,
  });
}

export function useMarketplaceCategories() {
  const { authToken } = useAuth();
  const { callAPI } = useAPI();

  return useQuery({
    queryKey: ["marketplace", "categories"],
    queryFn: () => callAPI("marketplace/categories", { token: authToken }),
    staleTime: 30 * 60 * 1000,
    enabled: !!authToken,
  });
}

export function useMarketplaceProgramDetail(programId) {
  const { authToken } = useAuth();
  const { callAPI } = useAPI();

  return useQuery({
    queryKey: ["marketplace", "program", programId],
    queryFn: async () => {
      const data = await callAPI(`marketplace/${programId}`, {
        token: authToken,
      });
      return {
        ...data,
        enrollmentRequestStatus: data.enrollment_request_status ?? null,
      };
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!authToken && !!programId,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export function useStartDemoSimulation() {
  const { authToken } = useAuth();
  const { callAPI } = useAPI();

  return useMutation({
    mutationFn: (programId) =>
      callAPI("start_marketplace_demo_simulation", {
        method: "POST",
        token: authToken,
        body: { program_id: programId },
      }),
  });
}

export function useCreateEnrollmentRequest() {
  const { authToken } = useAuth();
  const { callAPI } = useAPI();

  return useMutation({
    mutationFn: (programId) =>
      callAPI("marketplace/enroll_request", {
        method: "POST",
        token: authToken,
        body: { program_id: programId },
      }),
  });
}

// Maps UI sort label to API sort param
export const SORT_TO_API = {
  "Most popular": "popular",
  "Highest rated": "popular",
  Newest: "newest",
};
